import "./AppPayBankAccounts.css";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import { CheckBankAccount, Workspace, CheckBankAccountStatus, CHECK_BANK_ACCOUNT_STATUS, Company, CheckCompany, CheckResponseError, isCheckError } from "winslow-core";
import { 
    apiCheckListCompanyBankAccounts, 
    apiCheckCreateBankAccount,
    apiCheckUpdateBankAccount,
    apiCheckDeleteBankAccount,
} from "../../../services/PayrollService";
import { Button, Icon } from "../../../ds2/components";
import { Plus, Edit, Trash2, X } from "lucide-react";
import { ActionLink } from "../../../ds2/components/ActionLink";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import { Input, Label } from "../../../ds2/components";
import ModalConfirm from "../../policy-studio/components/ModalConfirm";
import { AppCheckErrorMessage } from "./AppCheckErrorMessage";
import { AppPayBankAccount } from "./AppPayBankAccount";

type AppPayBankAccountsProps = {
  company: Company | null;
  checkCompany: CheckCompany | null;
};

// Helper function to check if bank account is CheckBankAccountPlaidType
const isPlaidType = (account: CheckBankAccount): account is Extract<CheckBankAccount, { plaid_bank_account: any }> => {
    return "plaid_bank_account" in account;
};

// Helper function to check if bank account is CheckBankAccountRawType
const isRawType = (account: CheckBankAccount): account is Extract<CheckBankAccount, { raw_bank_account: any }> => {
    return "raw_bank_account" in account;
};

export const AppPayBankAccounts = ({ company, checkCompany }: AppPayBankAccountsProps) => {
    const dispatch = useDispatch();

    const uiStates = useSelector(selectUIStates);
    const storeUser = useSelector(selectUser);

    const workspace = storeUser?.workspaces.find((w: Workspace) => w.id === storeUser.currentWorkspaceId);
    const checkCompanyId = company?.checkCompanyId || checkCompany?.id;

    const [checkBankAccounts, setCheckBankAccounts] = useState<CheckBankAccount[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [showAddForm, setShowAddForm] = useState<boolean>(false);
    const [editingBankAccount, setEditingBankAccount] = useState<CheckBankAccount | null>(null);
    const [expandedAccountIds, setExpandedAccountIds] = useState<Set<string>>(new Set());
    const [fullAccountNumbers, setFullAccountNumbers] = useState<Map<string, string>>(new Map());
    const [isEditingAccountNumber, setIsEditingAccountNumber] = useState<boolean>(false);
    const [originalAccountNumberLastFour, setOriginalAccountNumberLastFour] = useState<string>("");
    const [originalFormData, setOriginalFormData] = useState<{
        institution_name: string;
        routing_number: string;
        subtype: "checking" | "savings";
    } | null>(null);
    const [bankAccountToDelete, setBankAccountToDelete] = useState<CheckBankAccount | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

    const [checkResponseError, setCheckResponseError] = useState<CheckResponseError | undefined>(undefined);

    // Form data for creating/editing bank accounts
    // Note: Bank accounts are complex - this is a simplified form
    // In reality, bank accounts are typically created through Plaid or manual entry
    const [formData, setFormData] = useState<{
        institution_name: string;
        account_number: string;
        routing_number: string;
        subtype: "checking" | "savings";
    }>({
        institution_name: "",
        account_number: "",
        routing_number: "",
        subtype: "checking",
    });

    // Bank account status options
    const statusOptions: GenericSelectOption[] = CHECK_BANK_ACCOUNT_STATUS.map((status) => ({
        displayName: status.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" "),
        value: status,
    }));

    // Subtype options
    const subtypeOptions: GenericSelectOption[] = [
        { displayName: "Checking", value: "checking" },
        { displayName: "Savings", value: "savings" },
    ];

    // Sort bank accounts by institution name
    const sortedBankAccounts = useMemo(() => {
        return [...checkBankAccounts].sort((a, b) => {
            const nameA = isPlaidType(a) ? a.plaid_bank_account.institution_name : (isRawType(a) ? a.raw_bank_account.institution_name || "" : "");
            const nameB = isPlaidType(b) ? b.plaid_bank_account.institution_name : (isRawType(b) ? b.raw_bank_account.institution_name || "" : "");
            return nameA.localeCompare(nameB);
        });
    }, [checkBankAccounts]);

    useEffect(() => {
        dispatch(setUIStates({...uiStates, hrisLocation: "Payroll Configuration"}));
        loadCheckBankAccounts();
    }, [storeUser, checkCompanyId]);

    const loadCheckBankAccounts = async () => {
        if (!checkCompanyId) return;
        
        setLoading(true);
        try {
            const apiResponse = await apiCheckListCompanyBankAccounts(checkCompanyId);
            const bankAccounts = apiResponse?.data;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse || isCheckError(apiResponse) || !bankAccounts) {
                toast("Error loading bank accounts", toastFail);
                setLoading(false);
                return;
            }

            setCheckBankAccounts(bankAccounts || []);
        } catch (error) {
            console.error("[AppPayBankAccounts] Error loading bank accounts:", error);
            toast("Error loading bank accounts", toastFail);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setEditingBankAccount(null);
        setShowAddForm(false);
        setIsEditingAccountNumber(false);
        setOriginalAccountNumberLastFour("");
        setOriginalFormData(null);
        setCheckResponseError(undefined);
    };

    const handleAddClick = () => {
        resetForm();
        setShowAddForm(true);
    };

    const handleEditClick = (bankAccount: CheckBankAccount) => {
        // Only allow editing raw bank accounts
        if (!isRawType(bankAccount)) {
            return;
        }

        // For raw accounts, we only have last 4, so we'll show that
        // In a real scenario, you might want to fetch the full account number separately
        const lastFour = bankAccount.raw_bank_account.account_number_last_four || "";
        const routingNumber = (bankAccount.raw_bank_account.routing_number || "").replace(/\D/g, "");
        const originalData = {
            institution_name: bankAccount.raw_bank_account.institution_name || "",
            routing_number: routingNumber,
            subtype: bankAccount.raw_bank_account.subtype,
        };
        
        setFormData({
            institution_name: originalData.institution_name,
            account_number: lastFour,
            routing_number: originalData.routing_number,
            subtype: originalData.subtype,
        });
        
        setEditingBankAccount(bankAccount);
        setShowAddForm(true);
        setIsEditingAccountNumber(false);
        setOriginalAccountNumberLastFour(lastFour);
        setOriginalFormData(originalData);
    };

    const handleDeleteClick = (bankAccount: CheckBankAccount) => {
        setBankAccountToDelete(bankAccount);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        if (!bankAccountToDelete) {
            return;
        }

        if (!checkCompanyId) {
            toast("Check company ID not found", toastFail);
            setShowDeleteConfirm(false);
            setBankAccountToDelete(null);
            return;
        }

        try {

            const apiResponse = await apiCheckDeleteBankAccount(bankAccountToDelete.id);
            const success = apiResponse?.data ?? false;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse || isCheckError(apiResponse) || !success) {
                toast("Error deleting bank account", toastFail);
                return;
            }

            if (success) {
                toast("Bank account deleted successfully", toastSuccess);
                await loadCheckBankAccounts();
            } else {
                toast("Error deleting bank account", toastFail);
            }
        } catch (error) {
            console.error("[AppPayBankAccounts] Error deleting bank account:", error);
            toast("Error deleting bank account", toastFail);
        } finally {
            setShowDeleteConfirm(false);
            setBankAccountToDelete(null);
        }
    };

    const handleBankAccountUpdate = async (updatedBankAccount: CheckBankAccount) => {
        // Bank account was already updated by AppPayBankAccount component
        // Just reload the list and close the form
        await loadCheckBankAccounts();
        resetForm();
    };

    const handleBankAccountCreate = async (createdBankAccount: CheckBankAccount) => {
        // Bank account was already created by AppPayBankAccount component
        // Just reload the list and close the form
        await loadCheckBankAccounts();
        resetForm();
    };

    const formatStatus = (status: CheckBankAccountStatus): string => {
        const statusOptions: GenericSelectOption[] = CHECK_BANK_ACCOUNT_STATUS.map((status) => ({
            displayName: status.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" "),
            value: status,
        }));
        const option = statusOptions.find(opt => opt.value === status);
        return option?.displayName || status;
    };

    const getInstitutionName = (account: CheckBankAccount): string => {
        if (isPlaidType(account)) {
            return account.plaid_bank_account.institution_name || "-";
        } else if (isRawType(account)) {
            return account.raw_bank_account.institution_name || "-";
        }
        return "-";
    };

    const getAccountNumber = (account: CheckBankAccount): string => {
        if (isPlaidType(account)) {
            return `****${account.plaid_bank_account.mask}`;
        } else if (isRawType(account)) {
            return `****${account.raw_bank_account.account_number_last_four}`;
        }
        return "-";
    };

    const handleAccountNumberClick = async (bankAccount: CheckBankAccount) => {
        

        // NR> for now prevent this as we don't have access

        // const accountId = bankAccount.id;
        // const isExpanded = expandedAccountIds.has(accountId);

        // if (isExpanded) {
        //     const newExpanded = new Set(expandedAccountIds);
        //     newExpanded.delete(accountId);
        //     setExpandedAccountIds(newExpanded);
        // } else {
        //     const newExpanded = new Set(expandedAccountIds);
        //     newExpanded.add(accountId);
        //     setExpandedAccountIds(newExpanded);

        //     // Fetch full account number if we don't have it cached
        //     if (!fullAccountNumbers.has(accountId)) {
        //         try {
        // NR>>>> put in the new ApiResponse =  format here
        //             const fullNumber = await apiCheckGetBankAccountNumber(accountId);
        //             if (fullNumber) {
        //                 const newFullNumbers = new Map(fullAccountNumbers);
        //                 newFullNumbers.set(accountId, fullNumber);
        //                 setFullAccountNumbers(newFullNumbers);
        //             }
        //         } catch (error) {
        //             console.error("[AppPayBankAccounts] Error fetching full account number:", error);
        //             toast("Error fetching account number", toastFail);
        //         }
        //     }
        // }
    };

    const getDisplayAccountNumber = (account: CheckBankAccount): string => {
        const accountId = account.id;
        const isExpanded = expandedAccountIds.has(accountId);

        if (isExpanded && fullAccountNumbers.has(accountId)) {
            return fullAccountNumbers.get(accountId) || getAccountNumber(account);
        }

        return getAccountNumber(account);
    };

    const getSubtype = (account: CheckBankAccount): string => {
        if (isPlaidType(account)) {
            return account.plaid_bank_account.subtype.charAt(0).toUpperCase() + account.plaid_bank_account.subtype.slice(1);
        } else if (isRawType(account)) {
            return account.raw_bank_account.subtype.charAt(0).toUpperCase() + account.raw_bank_account.subtype.slice(1);
        }
        return "-";
    };

    return (
        <div className="app-pay-bank-accounts">
            <AppCheckErrorMessage checkResponseError={checkResponseError} />
            
            <div className="app-pay-bank-accounts-header">
                {!showAddForm && (
                    <ActionLink
                        label="Add Bank Account"
                        leftIcon={<Plus size={16} />}
                        onClick={handleAddClick}
                    />
                )}
            </div>

            {showAddForm && (
                <AppPayBankAccount
                    bankAccount={editingBankAccount}
                    checkCompanyId={checkCompanyId}
                    onClose={resetForm}
                    onUpdate={handleBankAccountUpdate}
                    onCreate={handleBankAccountCreate}
                    onError={(error) => setCheckResponseError(error)}
                />
            )}

            {!showAddForm && (
                <>
                    {loading ? (
                        <div className="app-pay-bank-accounts-loading">
                            Loading bank accounts...
                        </div>
                    ) : sortedBankAccounts.length === 0 ? (
                        <div className="app-pay-bank-accounts-empty">
                            <p>No bank accounts found. Click "Add Bank Account" to create one.</p>
                        </div>
                    ) : (
                        <div className="app-pay-bank-accounts-table-container">
                            <table className="app-pay-bank-accounts-table">
                                <thead>
                                    <tr>
                                        <th>Institution</th>
                                        <th>Account Number</th>
                                        <th>Type</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedBankAccounts.map((bankAccount) => (
                                        <tr key={bankAccount.id} className="app-pay-bank-accounts-row">
                                            <td className="app-pay-bank-accounts-institution-cell">
                                                <strong>{getInstitutionName(bankAccount)}</strong>
                                            </td>
                                            <td 
                                                className="app-pay-bank-accounts-account-cell app-pay-bank-accounts-account-clickable"
                                                onClick={() => handleAccountNumberClick(bankAccount)}
                                                title={expandedAccountIds.has(bankAccount.id) ? "Click to hide full number" : "Click to show full number"}
                                            >
                                                {getDisplayAccountNumber(bankAccount)}
                                            </td>
                                            <td className="app-pay-bank-accounts-type-cell">
                                                {getSubtype(bankAccount)}
                                            </td>
                                            <td className="app-pay-bank-accounts-status-cell">
                                                {formatStatus(bankAccount.status)}
                                            </td>
                                            <td className="app-pay-bank-accounts-actions-cell">
                                                <div className="app-pay-bank-accounts-actions-group">
                                                    <Icon
                                                        variant="tertiary"
                                                        size="md"
                                                        icon={<Edit size={16} />}
                                                        onClick={() => isRawType(bankAccount) && handleEditClick(bankAccount)}
                                                        title={isRawType(bankAccount) ? "Edit" : "You can not edit Plaid bank accounts"}
                                                        disabled={!isRawType(bankAccount)}
                                                    />
                                                    <Icon
                                                        variant="tertiary"
                                                        size="md"
                                                        icon={<Trash2 size={16} />}
                                                        onClick={() => handleDeleteClick(bankAccount)}
                                                        title="Delete"
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            <ModalConfirm
                title="Delete Bank Account"
                text={bankAccountToDelete ? 
                    `Are you sure you want to delete the bank account for "${getInstitutionName(bankAccountToDelete)}" (${getDisplayAccountNumber(bankAccountToDelete)})? This action cannot be undone.` : 
                    ""
                }
                isOpen={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false);
                    setBankAccountToDelete(null);
                }}
                onContinue={handleDeleteConfirm}
            />
        </div>
    );
};

